import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * 칩 목록을 컨테이너 너비에 맞춰 보여준다.
 * 전부 들어가지 않으면 들어가는 개수만 남기고 마지막에 "…" 칩을 붙인다.
 *
 * 측정용 숨은 행을 함께 렌더해 항상 전체 칩의 실제 너비를 잰다.
 * (보이는 행만으로 재면 이미 잘린 상태라 다시 늘릴 수 없다)
 *
 * @param {string[]} items 칩 라벨 목록
 * @param {function} renderPill (label, key) => ReactNode
 * @param {string} className 컨테이너 클래스 (flex/gap 포함)
 */
const KeywordPillList = ({ items = [], renderPill, className = '' }) => {
  const itemsKey = items.join('|');
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const measureRow = measureRef.current;
    if (!container || !measureRow) return;

    const available = container.clientWidth;
    if (!available) return;

    const children = Array.from(measureRow.children);
    if (!children.length) return;

    // 마지막 요소는 "…" 칩 (측정용)
    const pillNodes = children.slice(0, -1);
    const ellipsisWidth = children[children.length - 1].offsetWidth;

    const gap = parseFloat(window.getComputedStyle(container).columnGap) || 0;

    const total = pillNodes.reduce(
      (sum, node, index) => sum + node.offsetWidth + (index > 0 ? gap : 0),
      0,
    );

    if (total <= available) {
      setVisibleCount(pillNodes.length);
      return;
    }

    let used = 0;
    let count = 0;
    for (let i = 0; i < pillNodes.length; i += 1) {
      const next = used + pillNodes[i].offsetWidth + (i > 0 ? gap : 0);
      // 뒤에 "…" 칩이 붙을 자리를 남겨둔다
      if (next + gap + ellipsisWidth > available) break;
      used = next;
      count += 1;
    }

    // 하나도 못 들어가면 첫 칩은 보여준다 (칩 자체가 말줄임 처리됨)
    setVisibleCount(Math.max(count, 1));
  }, []);

  useLayoutEffect(() => {
    measure();

    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, itemsKey]);

  if (!items.length) return <div className={className} />;

  const isTruncated = visibleCount < items.length;
  const hiddenLabels = items.slice(visibleCount).join(', ');

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {items.slice(0, visibleCount).map((item, index) =>
        renderPill(item, `${item}-${index}`),
      )}
      {isTruncated && renderPill('…', 'ellipsis', hiddenLabels)}

      {/* 측정 전용 (화면에 보이지 않음) */}
      <div
        ref={measureRef}
        aria-hidden='true'
        className='pointer-events-none invisible absolute left-0 top-0 flex gap-2'
      >
        {items.map((item, index) => renderPill(item, `measure-${index}`))}
        {renderPill('…', 'measure-ellipsis')}
      </div>
    </div>
  );
};

export default KeywordPillList;
